import { router } from '@inertiajs/vue3';
import { createGlobalState } from '@vueuse/core';
import { ref, onMounted } from 'vue';

/**
 * A Vue hook for tracking page navigation and events with Facebook Pixel.
 *
 * This hook initializes Facebook Pixel tracking on Inertia navigation events
 * and provides methods for standard and custom event tracking.
 *
 * @returns {Object} An object containing track and trackCustom methods
 *
 * @example
 *
 * import useFacebookPixel from '@/hooks/useFacebookPixel';
 *
 * // Inside a Vue component setup
 * const { track, trackCustom } = useFacebookPixel();
 *
 * // Track a standard event
 * track('Purchase', { value: 30, currency: 'USD' });
 *
 * // Track a custom event
 * trackCustom('UserEngagement', { action: 'clicked_feature' });
 */
const useFacebookPixel = createGlobalState(() => {
  const isInitialized = ref(false);

  /**
   * Tracks a standard Facebook Pixel event
   *
   * @param {string} eventName - Standard Facebook event name
   * @param {Object} [eventParams={}] - Optional parameters for the event
   * @returns {void}
   */
  const track = (eventName, eventParams = {}) => {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', eventName, eventParams);
    }
  };

  /**
   * Tracks a custom Facebook Pixel event
   *
   * @param {string} eventName - Custom event name
   * @param {Object} [eventParams={}] - Optional parameters for the event
   * @returns {void}
   */
  const trackCustom = (eventName, eventParams = {}) => {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('trackCustom', eventName, eventParams);
    }
  };

  /**
   * Initializes event listeners for page navigation
   */
  const initEventListeners = () => {
    if (isInitialized.value) return;

    router.on('navigate', (event) => {
      const pageData = event?.detail?.page || {};
      track('PageView', pageData);
    });

    isInitialized.value = true;
  };

  // Initialize the event listeners when the hook is first used
  onMounted(() => {
    initEventListeners();
  });

  return {
    track,
    trackCustom,
    initEventListeners, // Exposed for manual initialization if needed
  };
});

export default useFacebookPixel;
